import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { useBranch } from '../contexts/BranchContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar, CheckCircle, XCircle, Warning } from '@phosphor-icons/react';
import api, { getErrorMessage } from '../utils/api';
import { toast } from 'sonner';

const SubscriptionPage = () => {
  const { user } = useAuth();
  const { activeBranch } = useBranch();
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceInput, setPriceInput] = useState('');
  const isAdmin = user?.role === 'admin';

  const yearlyAmount = subscriptionStatus?.plans?.yearly?.amount ?? 1980000;
  const yearlyPrice = Math.round(yearlyAmount / 100);

  const saveYearlyPrice = async () => {
    const rupees = parseInt(priceInput, 10);
    if (!rupees || rupees < 1) { toast.error('Enter a valid price'); return; }
    try {
      await api.put('/billing/subscription/yearly-price', { price_rupees: rupees });
      toast.success(`Yearly price updated to ₹${rupees.toLocaleString('en-IN')}`);
      setEditingPrice(false);
      fetchSubscriptionStatus();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update price'));
    }
  };

  const PLANS = {
    monthly: {
      name: 'Monthly Subscription',
      amount: 165000,
      price: 1650,
      duration: '30 days',
      key: 'monthly'
    },
    yearly: {
      name: 'Yearly Subscription',
      amount: 1980000,
      price: 19800,
      duration: '365 days',
      key: 'yearly'
    }
  };

  useEffect(() => {
    if (activeBranch?.branch_id) {
      fetchSubscriptionStatus();
    }
  }, [activeBranch?.branch_id]);

  useEffect(() => {
    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const fetchSubscriptionStatus = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/billing/subscription/status/${activeBranch.branch_id}`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      toast.error('Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (plan) => {
    if (!activeBranch?.branch_id) {
      toast.error('No active branch selected');
      return;
    }

    setPurchasing(true);
    try {
      // Create order
      const orderResponse = await api.post('/billing/subscription/create-order', {
        branch_id: activeBranch.branch_id,
        plan: plan
      });

      const { order_id, amount, currency } = orderResponse.data;
      const razorpayKeyId = process.env.REACT_APP_RAZORPAY_KEY_ID;

      const options = {
        key: razorpayKeyId,
        amount: amount,
        currency: currency,
        order_id: order_id,
        name: 'CERACARE Subscription',
        description: `${PLANS[plan].name} - ${PLANS[plan].duration}`,
        image: 'https://i.imgur.com/n5tjHFD.png',
        handler: async (response) => {
          try {
            const verifyResponse = await api.post('/billing/subscription/verify-payment', {
              branch_id: activeBranch.branch_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan: plan
            });

            toast.success(verifyResponse.data.message);
            fetchSubscriptionStatus();
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
          contact: activeBranch?.phone || ''
        },
        notes: {
          branch_id: activeBranch.branch_id,
          plan: plan
        },
        theme: {
          color: '#2C4C3B'
        },
        modal: {
          ondismiss: function() {
            setPurchasing(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

      razorpay.on('payment.failed', (response) => {
        console.error('Payment Failed:', response.error);
        const errorMsg = response.error?.description || response.error?.reason || 'Payment failed';
        toast.error(`Payment Failed: ${errorMsg}`);
        setPurchasing(false);
      });

    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to initiate payment'));
      setPurchasing(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>,
      grace_period: <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Grace Period</Badge>,
      expired: <Badge className="bg-red-100 text-red-700 border-red-200">Expired</Badge>,
      not_subscribed: <Badge className="bg-gray-100 text-gray-700 border-gray-200">Not Subscribed</Badge>,
      not_required: <Badge className="bg-blue-100 text-blue-700 border-blue-200">Legacy (No Subscription Required)</Badge>
    };
    return badges[status] || badges.not_subscribed;
  };

  const getStatusIcon = (status) => {
    const icons = {
      active: <CheckCircle size={24} className="text-green-600" weight="fill" />,
      grace_period: <Warning size={24} className="text-yellow-600" weight="fill" />,
      expired: <XCircle size={24} className="text-red-600" weight="fill" />,
      not_subscribed: <XCircle size={24} className="text-gray-600" weight="fill" />,
      not_required: <CheckCircle size={24} className="text-blue-600" weight="fill" />
    };
    return icons[status] || icons.not_subscribed;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-xl text-gray-500">Loading subscription status...</div>
        </div>
      </Layout>
    );
  }

  const isBlocked = subscriptionStatus?.subscription_status === 'expired' && 
                    subscriptionStatus?.subscription_required;

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-[#1C1C1A]">Subscription Management</h1>
          <p className="text-gray-600 mt-2">Manage your CERACARE subscription</p>
        </div>

        {/* Current Status Card */}
        <Card className="p-6 mb-6 bg-gradient-to-r from-[#E2E8E4]/30 to-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {getStatusIcon(subscriptionStatus?.subscription_status)}
              <div>
                <h2 className="text-xl font-semibold text-[#1C1C1A]">Current Status</h2>
                <div className="mt-2">
                  {getStatusBadge(subscriptionStatus?.subscription_status)}
                </div>
              </div>
            </div>
            {subscriptionStatus?.subscription_required && subscriptionStatus?.subscription_end_date && (
              <div className="text-right">
                <div className="text-sm text-gray-600">Valid Until</div>
                <div className="text-lg font-semibold text-[#2C4C3B]">
                  {new Date(subscriptionStatus.subscription_end_date).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </div>
                {subscriptionStatus.days_remaining > 0 && (
                  <div className="text-sm text-gray-600 mt-1">
                    {subscriptionStatus.days_remaining} days remaining
                  </div>
                )}
                {subscriptionStatus.grace_days_remaining !== null && (
                  <div className="text-sm text-yellow-600 mt-1 font-medium">
                    Grace period: {subscriptionStatus.grace_days_remaining} days left
                  </div>
                )}
              </div>
            )}
          </div>

          {subscriptionStatus?.subscription_status === 'grace_period' && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-2 text-yellow-800">
                <Warning size={20} weight="fill" />
                <span className="font-medium">Your subscription has expired!</span>
              </div>
              <p className="text-sm text-yellow-700 mt-1">
                You have {subscriptionStatus.grace_days_remaining} days of grace period remaining. 
                Please renew to continue using the app without interruption.
              </p>
            </div>
          )}

          {subscriptionStatus?.subscription_status === 'not_required' && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 text-blue-800">
                <CheckCircle size={20} weight="fill" />
                <span className="font-medium">Legacy Branch - Full Access</span>
              </div>
              <p className="text-sm text-blue-700 mt-1">
                This branch has unlimited access without requiring a subscription.
              </p>
            </div>
          )}
        </Card>

        {/* Subscription Plans */}
        {(subscriptionStatus?.subscription_required || subscriptionStatus?.subscription_status !== 'not_required') && (
          <>
            <h2 className="text-2xl font-bold text-[#1C1C1A] mb-4">
              {subscriptionStatus?.subscription_status === 'active' ? 'Renew Subscription' : 'Choose Your Plan'}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Yearly Plan (monthly plan retired) */}
              <Card className="p-6 hover:shadow-lg transition-shadow border-2 border-[#2C4C3B] bg-white md:col-start-1" data-testid="yearly-plan-card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-[#1C1C1A]">Yearly</h3>
                  <Calendar size={32} className="text-[#2C4C3B]" weight="duotone" />
                </div>
                <div className="mb-4">
                  {editingPrice ? (
                    <div className="flex items-center gap-2" data-testid="yearly-price-editor">
                      <span className="text-2xl font-bold text-[#2C4C3B]">₹</span>
                      <input
                        type="number" min="1" value={priceInput} onChange={(e) => setPriceInput(e.target.value)}
                        data-testid="yearly-price-input"
                        className="w-40 border border-[#2C4C3B] rounded-md px-3 py-1.5 text-2xl font-bold text-[#2C4C3B] focus:outline-none"
                      />
                      <Button size="sm" data-testid="yearly-price-save" onClick={saveYearlyPrice} className="bg-[#2C4C3B] text-white">Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingPrice(false)}>Cancel</Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className="text-4xl font-bold text-[#2C4C3B]" data-testid="yearly-price">₹{yearlyPrice.toLocaleString('en-IN')}</div>
                      {isAdmin && (
                        <button
                          type="button"
                          data-testid="edit-yearly-price-btn"
                          onClick={() => { setPriceInput(String(yearlyPrice)); setEditingPrice(true); }}
                          className="text-xs font-medium text-[#C05C42] border border-[#C05C42]/40 rounded px-2 py-1 hover:bg-[#C05C42] hover:text-white transition-colors"
                        >
                          Edit price
                        </button>
                      )}
                    </div>
                  )}
                  <div className="text-sm text-gray-600 mt-1">per year</div>
                </div>
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle size={16} className="text-green-600" weight="fill" />
                    <span>365 days of full app access</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle size={16} className="text-green-600" weight="fill" />
                    <span>All features included</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle size={16} className="text-green-600" weight="fill" />
                    <span>3-day grace period</span>
                  </div>
                </div>
                <Button
                  onClick={() => handleSubscribe('yearly')}
                  disabled={purchasing}
                  className="w-full bg-[#2C4C3B] hover:bg-[#1f3529] text-white"
                >
                  {purchasing ? 'Processing...' : 'Subscribe Yearly'}
                </Button>
              </Card>
            </div>

            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h3 className="font-semibold text-[#1C1C1A] mb-2">What&apos;s Included:</h3>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Full access to CERACARE CRM platform</li>
                <li>• Customer management and batch scheduling</li>
                <li>• Attendance tracking and stock management</li>
                <li>• Company ledger and financial reports</li>
                <li>• Multi-branch support (if applicable)</li>
                <li>• AI features require separate credit purchase at ₹1.50/credit</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default SubscriptionPage;
